import { type ReactNode, useState } from 'react'
import cx from 'classnames'
import { MinidownContent } from '@/components/MinidownContent'
import { Avatar } from '@/components/Avatar'
import { Icon } from '@/components/Icon'
import { RawButton } from '@/components/Button'
import { IconButton } from '@/components/IconButton'
import { Link } from '@/components/Link'
import { AutoSkeleton } from '@/components/Skeleton'
import { timeago } from '@/utils/date-util'
import { links } from '@/utils/link'
import styles from './NotificationsPage.module.css'
import logoWorklayerBlueUrl from '@/assets/misc/logo_worklayer-blue.svg'

export interface NotificationsPageProps {
  listVisible: boolean
  detailVisible: boolean
  listHeader?: ReactNode
  threadList?: ReactNode
  detail?: ReactNode
}

/**
 * Notifications on the left; the job a notification is about opens on the
 * right, in place of job details' own job list.
 */
export function NotificationsPage({
  listVisible,
  detailVisible,
  listHeader,
  threadList,
  detail,
}: NotificationsPageProps) {
  return (
    <div className={styles.root}>
      {listVisible && (
        <aside className={styles.list} aria-label="Notifications">
          {listHeader}
          {threadList}
        </aside>
      )}
      {detailVisible && <main className={styles.detail}>{detail}</main>}
    </div>
  )
}

export interface NotificationsListHeaderProps {
  workspaceSelector?: ReactNode
  isMarkAllAsReadDisabled: boolean
  onMarkAllAsRead: () => unknown
}

export function NotificationsListHeader({
  workspaceSelector,
  isMarkAllAsReadDisabled,
  onMarkAllAsRead,
}: NotificationsListHeaderProps) {
  return (
    <div className={styles.header}>
      <div className={styles.heading}>{workspaceSelector}</div>
      <IconButton
        name="checkmark-outline"
        label="Mark all as read"
        variant="ghost"
        isDisabled={isMarkAllAsReadDisabled}
        onPress={onMarkAllAsRead}
        size={15}
      />
      <IconButton
        name="settings-outline"
        label="Notification preferences"
        variant="ghost"
        to={links.profileNotifications()}
        size={15}
      />
    </div>
  )
}

export interface ThreadListProps {
  children?: ReactNode
  loader?: ReactNode
}

export function ThreadList({ children, loader }: ThreadListProps) {
  return (
    <div className={styles.scroll}>
      {children}
      {loader}
    </div>
  )
}

const PREVIEW_COUNT = 5

export interface ThreadItemEntry {
  id: string
  isRead: boolean
  plainText: string
  dispatchedAt: Date
}

export interface ThreadItemProps {
  selected: boolean
  onOpen: () => unknown
  to: string | null
  unread: boolean
  imageSource: string | null | undefined
  /**
   * `null` when the notification is not about a loaded job.
   */
  jobDisplayName: string | null
  text: string
  plainText: string
  dispatchedAt: Date
  context: string
  earlier: ThreadItemEntry[]
}

export function ThreadItem({
  selected,
  onOpen,
  to,
  unread,
  imageSource,
  jobDisplayName,
  text,
  plainText,
  dispatchedAt,
  context,
  earlier,
}: ThreadItemProps) {
  const [expanded, setExpanded] = useState(false)
  const [showAll, setShowAll] = useState(false)
  const shown = showAll ? earlier : earlier.slice(0, PREVIEW_COUNT)
  const hasJob = jobDisplayName !== null
  return (
    <div className={styles.thread}>
      <div
        className={cx(
          styles.item,
          unread && styles.unread,
          selected && styles.selected,
        )}
      >
        <Avatar
          size={28}
          subject={{
            avatar: imageSource || logoWorklayerBlueUrl,
          }}
        />
        <div className={styles.content}>
          <div className={styles.titleRow}>
            {unread && <span className={styles.dot} />}
            <Link
              className={cx(styles.title, !hasJob && styles.messageTitle)}
              to={to || undefined}
              onClick={onOpen}
              aria-current={selected ? 'page' : undefined}
              title={hasJob ? jobDisplayName : plainText}
            >
              {hasJob ? jobDisplayName : <MinidownContent value={text} />}
            </Link>
            <time className={styles.time} dateTime={dispatchedAt.toISOString()}>
              {timeago(dispatchedAt)}
            </time>
          </div>
          {hasJob && (
            <p className={styles.event} title={plainText}>
              <MinidownContent value={text} />
            </p>
          )}
          <div className={styles.meta}>
            <span className={styles.context}>{context}</span>
            {earlier.length > 0 && (
              <>
                <span aria-hidden="true">·</span>
                <RawButton
                  className={styles.expand}
                  aria-expanded={expanded}
                  onPress={() => setExpanded(!expanded)}
                >
                  {earlier.length} earlier
                  <Icon
                    name="chevron-down"
                    size={12}
                    rotate={expanded ? 180 : undefined}
                  />
                </RawButton>
              </>
            )}
          </div>
        </div>
      </div>
      {expanded && (
        <ol className={styles.timeline}>
          {shown.map((item) => (
            <li
              key={item.id}
              className={cx(styles.timelineItem, !item.isRead && styles.unread)}
            >
              <span className={styles.timelineDot} />
              <p title={item.plainText}>{item.plainText}</p>
              <time
                className={styles.time}
                dateTime={item.dispatchedAt.toISOString()}
              >
                {timeago(item.dispatchedAt)}
              </time>
            </li>
          ))}
          {earlier.length > shown.length && (
            <li className={styles.timelineMore}>
              <RawButton
                className={styles.timelineMoreButton}
                onPress={() => setShowAll(true)}
              >
                Show {earlier.length - shown.length} more
              </RawButton>
            </li>
          )}
        </ol>
      )}
    </div>
  )
}

export interface NoJobSelectedProps {
  text: string
  isSkeleton?: boolean
}

export function NoJobSelected({ text, isSkeleton }: NoJobSelectedProps) {
  return (
    <div className={styles.empty}>
      <Icon name="inbox" size={56} color="var(--text-quaternary)" />
      <p>
        <AutoSkeleton isSkeleton={isSkeleton}>{text}</AutoSkeleton>
      </p>
    </div>
  )
}
